import { MouseEvent, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DownloadDoneIcon from '@mui/icons-material/DownloadDone';
import DownloadingIcon from '@mui/icons-material/Downloading';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { find } from 'lodash-es';
import { useConfirm } from 'material-ui-confirm';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { useAuth } from 'src/auth/useAuth';
import DashboardListPageLayout from 'src/common/components/dashboard/DashboardListPageLayout';
import ListFilterEmptyState from 'src/common/components/dashboard/ListFilterEmptyState';
import Empty from 'src/common/components/Empty';
import { ticketCardVirtualizedContainerSx } from 'src/common/styles/ticketCardStyles';
import Icon from 'src/common/components/icons/Icon';
import { dashboardPrimaryActionButtonProps } from 'src/common/components/DashboardPageToolbar';
import QrCheckinActionDialog from 'src/common/components/QrCheckinActionDialog';
import QrCheckinScannerDialog from 'src/common/components/QrCheckinScannerDialog';
import UserCheckinWizardOpenButton from 'src/common/components/UserCheckinWizardOpenButton';
import CheckinModal from 'src/common/modals/CheckinModal';
import CheckinSerialModal from 'src/common/modals/CheckinSerialModal';
import SignupTimelineSection from 'src/common/components/signup-timeline/SignupTimelineSection';
import { LanguageKeys } from 'src/common/constants/languages';
import {
  filterAdminParticipants,
  getParticipantRegistrationState,
  getRegistrationIdsFromParticipants,
  registrationSignupAt,
  sortParticipants,
} from 'src/helpers/participantList';
import {
  getCreatedParticipantFromSaveResponse,
  shouldOfferRegistrationAfterParticipantCreate,
} from 'src/helpers/participantCreateRegistrationPrompt';
import { resolveCheckinApplicantAfterRegistration } from 'src/helpers/resolveCheckinApplicantAfterRegistration';
import { pointsFromRegistrationDates } from 'src/helpers/signupTimeline';
import { canRegisterParticipant } from 'src/helpers/userCheckinWizard';
import { confirmRegistrationCreate } from 'src/helpers/confirmRegistrationCreate';
import { getAxiosErrorMessage } from 'src/helpers/axiosError';
import { useCheckinModalState } from 'src/hooks/useCheckinModalState';
import { QrCheckinScanResult, useQrCheckinScan } from 'src/hooks/useQrCheckinScan';
import { useParticipantEditorModal } from 'src/hooks/useParticipantEditorModal';
import { useParticipantListFilters } from 'src/hooks/useParticipantListFilters';
import usePersistentView from 'src/hooks/usePersistentView';
import { useRegistrationModalState } from 'src/hooks/useRegistrationModalState';
import { IFormCheckin } from 'src/interfaces/Checkin';
import { IEnrichedParticipant, IFormParticipant, IParticipant } from 'src/interfaces/Participant';
import { IFormRegistration } from 'src/interfaces/Registration';
import { EView } from 'src/interfaces/View';
import VirtualizedParticipantGrid from 'src/pages/Dashboard/Participants/components/VirtualizedParticipantGrid';
import useUsersStore from 'src/stores/usersStore';
import useCheckinsStore from 'src/stores/checkinsStore';
import useParticipantsStore from 'src/stores/participantsStore';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import useTransfersStore from 'src/stores/transfersStore';
import ParticipantFilterForm from './forms/ParticipantFilterForm';
import ParticipantEditorModal from './modals/ParticipantEditorModal';
import RegistrationModal from './modals/RegistrationModal';
import TransferModal from './modals/TransferModal';
import UserCheckinWizardModal from './modals/UserCheckinWizardModal/UserCheckinWizardModal';
import ParticipantsTable from './tables/ParticipantsTable';
import useRegistrationsStore from 'src/stores/registrationsStore';
import AdminParticipantsTourOfferDialog from './components/AdminParticipantsTourOfferDialog';
import { useAdminParticipantsTour } from './hooks/useAdminParticipantsTour';

const Participants = () => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  const {
    accommodationsById,
    competitions,
    competitionsById,
    dateTimeFormat,
    distances,
    distancesById,
    editionsById,
    activeEditionId,
  } = useSystemOptionsStore(systemOptionsState => systemOptionsState);
  const {
    checkinsById,
    deleteCheckin: doDeleteCheckin,
    fetchCheckins: doFetchCheckins,
    isLoadingCheckins,
    saveCheckin: doSaveCheckin,
  } = useCheckinsStore(checkinsState => checkinsState);
  const {
    participants,
    deleteParticipant: doDeleteParticipant,
    deleteAllParticipants: doDeleteAllParticipants,
    fetchParticipants: doFetchParticipants,
    isLoadingParticipants,
    saveParticipantAsAdmin: doSaveParticipantAsAdmin,
  } = useParticipantsStore(participantsState => participantsState);
  const users = useUsersStore(s => s.users);
  const {
    createRegistrationAsAdmin,
    updateRegistrationAsAdmin,
    deleteRegistrationAsAdmin,
    isLoading: isLoadingRegistrations,
  } = useRegistrationsStore();
  const { isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = usePersistentView();
  const [isDownloading, setIsDownloading] = useState<'no' | 'yes' | 'finished'>('no');
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferParticipant, setTransferParticipant] = useState<IParticipant | undefined>();
  const [userCheckinWizardOpen, setUserCheckinWizardOpen] = useState(false);
  const [userCheckinWizardInitialUserId, setUserCheckinWizardInitialUserId] = useState<
    string | undefined
  >();
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [qrScanResult, setQrScanResult] = useState<QrCheckinScanResult | null>(null);

  const {
    query,
    competitionFilter,
    distanceFilter,
    checkedInFilter,
    registeredFrom,
    registeredTo,
    editionFilter,
    ownerUserIdFilter,
    order,
    orderByKey,
    setOrder,
    setOrderByKey,
    onChangeFilters,
  } = useParticipantListFilters({
    initialEditionId: activeEditionId || null,
    persistInUrl: true,
  });

  const fetchParticipantsOptions = useMemo(
    () => ({
      editionId: editionFilter || undefined,
      ownerUserId: ownerUserIdFilter || undefined,
    }),
    [editionFilter, ownerUserIdFilter],
  );
  const effectiveEditionId = editionFilter || activeEditionId || null;

  const {
    open: contestantEditorModalOpen,
    selectedParticipant,
    openEditor: handleOpenParticipantEditorModal,
    closeEditor: handleCloseParticipantEditorModal,
  } = useParticipantEditorModal(participants);

  const {
    open: registrationModalOpen,
    selectedParticipant: registrationParticipant,
    selectedRegistration,
    openForCreate: openRegistrationForCreate,
    openForCreateParticipant: openRegistrationForCreateParticipant,
    openForEdit: openRegistrationForEdit,
    close: handleCloseRegistrationModal,
  } = useRegistrationModalState();

  const {
    checkinModalOpen,
    selectedCheckinParticipant,
    newCheckin,
    setNewCheckin,
    openCheckin: handleOpenCheckinModal,
    openCheckinForParticipant,
    closeCheckin: handleCloseCheckinModal,
    clearNewCheckin,
  } = useCheckinModalState(participants);

  const handleOpenRegistrationModal = (id?: string, e?: MouseEvent<unknown>) => {
    openRegistrationForCreate(participants, id, e);
  };

  const sortedParticipants = useMemo(() => {
    const filtered = filterAdminParticipants(participants, {
      query,
      competitionFilter,
      distanceFilter,
      checkedInFilter,
      registeredFrom,
      registeredTo,
    });
    const editionIdForAge = editionFilter || activeEditionId;
    const edition =
      editionIdForAge && editionsById?.[editionIdForAge]
        ? editionsById[editionIdForAge]
        : null;
    return sortParticipants(filtered, orderByKey, order, {
      editionYear: edition?.year ?? null,
      editionDate: edition?.date,
    });
  }, [
    participants,
    query,
    competitionFilter,
    distanceFilter,
    checkedInFilter,
    registeredFrom,
    registeredTo,
    orderByKey,
    order,
    editionFilter,
    activeEditionId,
    editionsById,
  ]);

  const signupTimelinePoints = useMemo(
    () =>
      pointsFromRegistrationDates(
        (sortedParticipants as IEnrichedParticipant[]).map(p =>
          registrationSignupAt(
            p.registration?.status === 'cancelled' ? null : p.registration,
            p.createdAt,
          ),
        ),
      ),
    [sortedParticipants],
  );

  // Refetch participants when edition or owner filter changes
  useEffect(() => {
    doFetchParticipants(fetchParticipantsOptions);
  }, [fetchParticipantsOptions, doFetchParticipants]);

  const saveParticipant = async (editedParticipant: IFormParticipant, id?: string) => {
    // Always use the admin endpoint so system-page creates stay ownerless
    // (never POST /me/participants, which would attach the logged-in admin).
    await doSaveParticipantAsAdmin(editedParticipant, id)
      .then(response => {
        enqueueSnackbar(t('participants.saveSuccessful'), {
          variant: 'success',
        });
        doFetchParticipants(fetchParticipantsOptions);
        handleCloseParticipantEditorModal();

        if (
          !shouldOfferRegistrationAfterParticipantCreate(id, effectiveEditionId || undefined, editionsById, {
            requireSignupOpen: false,
          })
        ) {
          return;
        }

        const created = getCreatedParticipantFromSaveResponse(response);
        if (created?.id) {
          openRegistrationForCreateParticipant(created);
        }
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`race.signUp.errors.${errorCode}`) : message, {
            variant: 'error',
          });
        },
      );
  };

  const handleDeleteParticipant = async (id: string, e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    const { confirmed } = await confirm({
      title: t('participants.deleteConfirm'),
      description: t('participants.deleteDescription'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      enqueueSnackbar(t('participants.deleteErrorMessage'), {
        variant: 'error',
      });
      return;
    }
    await doDeleteParticipant(id)
      .then(() => {
        enqueueSnackbar(t('participants.deleteSuccessfulMessage'), {
          variant: 'success',
        });
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`participants.errors.${errorCode}`) : message, {
            variant: 'error',
          });
        },
      );
  };

  const handleDeleteAllParticipants = async (e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    const { confirmed } = await confirm({
      title: t('participants.deleteAllConfirm'),
      description: t('participants.deleteDescription'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      enqueueSnackbar(t('participants.deleteErrorMessage'), {
        variant: 'error',
      });
      return;
    }
    await doDeleteAllParticipants(fetchParticipantsOptions)
      .then(() => {
        enqueueSnackbar(t('participants.deleteSuccessfulMessage'), {
          variant: 'success',
        });
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`participants.errors.${errorCode}`) : message, {
            variant: 'error',
          });
        },
      );
  };

  const handleTransferParticipant = async (id: string, e?: MouseEvent<unknown>) => {
    e && e.stopPropagation();
    setTransferParticipant(find(participants, { id }));
    setTransferModalOpen(true);
  };

  const handleCloseTransferModal = () => {
    setTransferModalOpen(false);
    setTransferParticipant(undefined);
  };

  const saveTransfer = async (email: string, dateOfBirth: Date | null) => {
    if (!transferParticipant) return;
    if (!dateOfBirth) {
      enqueueSnackbar(t('participants.dateOfBirthRequired'), { variant: 'error' });
      return;
    }
    const { initParticipantTransfer } = useTransfersStore.getState();
    try {
      await initParticipantTransfer(transferParticipant.id, email, dateOfBirth);
      enqueueSnackbar(t('participants.transferInitiated'), { variant: 'success' });
      handleCloseTransferModal();
    } catch (err: unknown) {
      const message = getAxiosErrorMessage(err, t('participants.transferFailed'));
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleDeleteRegistration = async (id?: string, e?: MouseEvent<unknown>) => {
    e?.stopPropagation();
    const participant = find(sortedParticipants, { id }) as IEnrichedParticipant | undefined;
    const registrationIdToDelete = participant?.registration?.id;
    if (!registrationIdToDelete) {
      return;
    }
    if (participant.registration?.status === 'checked_in') {
      enqueueSnackbar(t('registrations.checkedInNotEditable'), { variant: 'error' });
      return;
    }
    const { confirmed } = await confirm({
      title: t('registrations.deleteRegistration'),
      description: t('registrations.deleteRegistrationConfirm'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      return;
    }
    await deleteRegistrationAsAdmin(registrationIdToDelete)
      .then(async () => {
        await doFetchParticipants(fetchParticipantsOptions);
        enqueueSnackbar(t('registrations.deleteRegistrationSuccessful'), { variant: 'success' });
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`registrations.errors.${errorCode}`) : message, {
            variant: 'error',
          });
        },
      );
  };

  const handleEditRegistrationWithGuard = (id?: string, e?: MouseEvent<unknown>) => {
    const participant = find(sortedParticipants, { id }) as IEnrichedParticipant | undefined;
    if (!participant?.registration) {
      return;
    }
    if (participant.registration.status === 'checked_in') {
      enqueueSnackbar(t('registrations.checkedInNotEditable'), { variant: 'error' });
      return;
    }
    openRegistrationForEdit(sortedParticipants as IEnrichedParticipant[], id, e);
  };

  const saveRegistration = async (registration: IFormRegistration) => {
    if (!effectiveEditionId || !registrationParticipant?.id) {
      enqueueSnackbar(t('registrations.noActiveEdition'), { variant: 'error' });
      return;
    }
    const existingPrice = (registrationParticipant as IEnrichedParticipant | undefined)
      ?.registration?.price;
    // On edit, only send price when the admin changed it — competition/distance
    // edits must keep the registration-time (or last overridden) price.
    const shouldSendPrice =
      isAdmin &&
      registration.price != null &&
      (!registration.id || registration.price !== existingPrice);
    const payload = {
      competitionId: registration.competitionId,
      distanceId: registration.distanceId,
      ...(shouldSendPrice ? { price: registration.price } : {}),
    };
    if (!registration.id) {
      const competition = find(competitions, c => c.id === registration.competitionId);
      const distance = find(distances, d => d.id === registration.distanceId);
      const confirmed = await confirmRegistrationCreate({
        confirm,
        t,
        participantName: `${registrationParticipant.lastName} ${registrationParticipant.firstName}`,
        competitionName: competition?.i18n[currentLang] || '',
        distanceName: distance?.i18n[currentLang] || '',
        price: registration.price,
      });
      if (!confirmed) {
        return;
      }
    }

    const savePromise = registration.id
      ? updateRegistrationAsAdmin(registration.id, payload)
      : createRegistrationAsAdmin(
        {
          participantId: registrationParticipant.id,
          editionId: effectiveEditionId,
          competitionId: registration.competitionId,
          distanceId: registration.distanceId,
          price: registration.price!,
        },
        currentLang,
      );
    await savePromise
      .then(async () => {
        const isCreate = !registration.id;
        const participantId = registrationParticipant?.id;
        await doFetchParticipants(fetchParticipantsOptions);
        enqueueSnackbar(t('participants.saveSuccessful'), { variant: 'success' });
        handleCloseRegistrationModal();
        if (!isCreate || !participantId || !effectiveEditionId) {
          return;
        }
        const refreshed = find(useParticipantsStore.getState().participants, {
          id: participantId,
        }) as IEnrichedParticipant | undefined;
        openCheckinForParticipant(
          resolveCheckinApplicantAfterRegistration({
            participantId,
            participant: registrationParticipant,
            registrationForm: registration,
            editionId: effectiveEditionId,
            editionYear: editionsById?.[effectiveEditionId]?.year,
            refreshed,
          }),
        );
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`registrations.errors.${errorCode}`) : message, { variant: 'error' });
        },
      );
  };

  const eventName =
    effectiveEditionId && editionsById?.[effectiveEditionId]
      ? `${t('base.defaultTitle')} ${editionsById[effectiveEditionId].year}`
      : undefined;

  const registrationIds = useMemo(
    () => getRegistrationIdsFromParticipants(sortedParticipants as IEnrichedParticipant[]),
    [sortedParticipants],
  );

  const adminTourAnchorParticipantId = sortedParticipants[0]?.id;

  const adminTourRegisterAnchorId = useMemo(
    () =>
      (sortedParticipants as IEnrichedParticipant[]).find(
        p => getParticipantRegistrationState(p) === 'unregistered' && canRegisterParticipant(p),
      )?.id,
    [sortedParticipants],
  );

  const adminTourCheckinAnchorId = useMemo(
    () =>
      (sortedParticipants as IEnrichedParticipant[]).find(
        p => getParticipantRegistrationState(p) === 'registered',
      )?.id,
    [sortedParticipants],
  );

  const {
    offerOpen: tourOfferOpen,
    startTour,
    handleOfferDecline: handleTourDecline,
    handleOfferStart: handleTourStart,
  } = useAdminParticipantsTour({
    ready: !isLoadingParticipants && !isLoadingCheckins && sortedParticipants.length > 0,
    isSuperAdmin,
    openRegistration: adminTourRegisterAnchorId
      ? () => handleOpenRegistrationModal(adminTourRegisterAnchorId)
      : undefined,
    closeRegistration: handleCloseRegistrationModal,
    openCheckin: adminTourCheckinAnchorId
      ? () => handleOpenCheckinModal(adminTourCheckinAnchorId)
      : undefined,
    closeCheckin: handleCloseCheckinModal,
  });

  const { handleScan: handleQrCheckinScan } = useQrCheckinScan({
    editionId: activeEditionId,
    onResolved: setQrScanResult,
    enqueueSnackbar,
    t,
    onSuccess: () => setQrScannerOpen(false),
  });

  const handleQrCheckInSingle = () => {
    if (!qrScanResult) {
      return;
    }
    if (qrScanResult.alreadyCheckedIn) {
      enqueueSnackbar(t('checkins.qrAlreadyCheckedIn'), { variant: 'error' });
      return;
    }
    const participantId = qrScanResult.participantId;
    setQrScanResult(null);
    handleOpenCheckinModal(participantId);
  };

  const handleQrCheckInAllForUser = () => {
    if (!qrScanResult?.ownerUserId) {
      enqueueSnackbar(t('checkins.qrNoAccountUser'), { variant: 'warning' });
      return;
    }
    setUserCheckinWizardInitialUserId(qrScanResult.ownerUserId);
    setQrScanResult(null);
    setUserCheckinWizardOpen(true);
  };

  const saveCheckin = async (checkin: IFormCheckin, id?: string) => {
    await doSaveCheckin(checkin, id)
      .then(async newCheckin => {
        await Promise.all([doFetchParticipants(fetchParticipantsOptions), doFetchCheckins()]);
        enqueueSnackbar(t('checkins.saveCheckinSuccess'), {
          variant: 'success',
        });
        setNewCheckin(newCheckin || undefined);
      })
      .catch(
        ({
          response: {
            data: { errorCode, message },
          },
        }) => {
          enqueueSnackbar(errorCode ? t(`checkins.errors.${errorCode}`) : message, {
            variant: 'error',
          });
        },
      )
      .finally(() => {
        handleCloseCheckinModal();
      });
  };

  const handleUpdatePrice = async (regId: string, price: number) => {
    await updateRegistrationAsAdmin(regId, { price })
      .then(() => doFetchParticipants(fetchParticipantsOptions))
      .then(() => enqueueSnackbar(t('participants.saveSuccessful'), { variant: 'success' }))
      .catch(() => enqueueSnackbar(t('base.genericError'), { variant: 'error' }));
  };

  const handleDeleteCheckin = async (checkinId: string, e?: MouseEvent<unknown>) => {
    e?.stopPropagation();
    const { confirmed } = await confirm({
      title: t('checkins.deleteCheckin'),
      description: t('checkins.deleteDescription'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      return;
    }
    await doDeleteCheckin(checkinId)
      .then(async () => {
        await Promise.all([doFetchParticipants(fetchParticipantsOptions), doFetchCheckins()]);
        enqueueSnackbar(t('checkins.deleteSuccessfulMessage'), { variant: 'success' });
      })
      .catch(
        ({
          response: {
            data: { message },
          },
        }) => {
          enqueueSnackbar(message || t('checkins.errors.cannotDeleteCheckin'), { variant: 'error' });
        },
      );
  };

  const exportMenu = usePopupState({ variant: 'popover', popupId: 'admin-participants-export-menu' });

  const exportToExcel = (list: IEnrichedParticipant[]) => {
    setIsDownloading('yes');
    void import('src/helpers/export')
      .then(({ exportParticipantsToExcel }) =>
        exportParticipantsToExcel({
          participants: list,
          competitionsById: competitionsById || {},
          distancesById: distancesById || {},
          accommodationsById: accommodationsById || {},
          editionsById: editionsById || {},
          checkinsById: checkinsById || {},
          currentLang,
          dateTimeFormat,
          translate: t,
          fileName: t('participants.adminPageTitle'),
          sheetName: t('participants.adminPageTitle'),
        }),
      )
      .catch(() => {
        enqueueSnackbar(t('participants.errors.exportFailed'), { variant: 'error' });
      })
      .finally(() => {
        setIsDownloading('finished');
      });
  };

  return (
    <>
      <DashboardListPageLayout
        title={`${t('participants.adminPageTitle')} (${sortedParticipants.length})`}
        isLoading={isLoadingParticipants || isLoadingCheckins}
        toolbarAsideVariant="full"
        headerActions={
          <>
            <Tooltip title={t('participants.adminTour.showAgain')}>
              <IconButton onClick={startTour} aria-label={t('participants.adminTour.showAgain')}>
                <HelpOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('participants.merge.pageTitle')}>
              <IconButton
                onClick={() => navigate('/dashboard/system/participants/merge')}
                aria-label={t('participants.merge.pageTitle')}
              >
                <CallMergeIcon />
              </IconButton>
            </Tooltip>
            {isSuperAdmin ? (
              <Tooltip title={t('participants.deleteAllParticipants')}>
                <IconButton
                  onClick={handleDeleteAllParticipants}
                  color="error"
                  aria-label={t('participants.deleteAllParticipants')}
                  data-tour="admin-participants-delete-all"
                >
                  <DeleteForeverIcon />
                </IconButton>
              </Tooltip>
            ) : null}
            <Button
              {...dashboardPrimaryActionButtonProps()}
              onClick={() => handleOpenParticipantEditorModal()}
              startIcon={<AddIcon />}
              data-tour="admin-participants-add"
            >
              {t('participants.new')}
            </Button>
            {isAdmin ? (
              <Tooltip
                title={
                  activeEditionId
                    ? t('checkins.scanQr')
                    : t('userCheckinWizard.noActiveEdition')
                }
              >
                <span>
                  <Button
                    {...dashboardPrimaryActionButtonProps()}
                    startIcon={<QrCodeScannerIcon />}
                    disabled={!activeEditionId}
                    onClick={() => setQrScannerOpen(true)}
                    data-tour="admin-participants-scan-qr"
                  >
                    {t('checkins.scanQr')}
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {isAdmin ? (
              <UserCheckinWizardOpenButton
                activeEditionId={activeEditionId}
                data-testid="user-checkin-wizard-open"
                data-tour="admin-participants-wizard"
                onClick={() => {
                  setUserCheckinWizardInitialUserId(undefined);
                  setUserCheckinWizardOpen(true);
                }}
              />
            ) : null}
          </>
        }
        toolbar={
          <ParticipantFilterForm
            checkedInFilter={checkedInFilter}
            competitionFilter={competitionFilter}
            distanceFilter={distanceFilter}
            editionFilter={editionFilter}
            registeredFrom={registeredFrom}
            registeredTo={registeredTo}
            ownerUserIdFilter={ownerUserIdFilter}
            users={users}
            onChange={onChangeFilters}
            onSetView={setView}
            query={query}
            view={view}
            order={order}
            setOrder={setOrder}
            orderByKey={orderByKey}
            setOrderByKey={setOrderByKey}
            tourPrefix="admin-participants"
            filterButtonDataTour="admin-participants-filter-btn"
            filterPopoverDataTour="admin-participants-filter-panel"
            searchInToolbar
            toolbarActions={
                <>
                  <Button
                    {...dashboardPrimaryActionButtonProps()}
                    {...bindTrigger(exportMenu)}
                    data-tour="admin-participants-export"
                    startIcon={
                      isDownloading === 'yes' ? (
                        <DownloadingIcon />
                      ) : isDownloading === 'finished' ? (
                        <DownloadDoneIcon />
                      ) : (
                        <Icon icon="xml" />
                      )
                    }
                    endIcon={<ArrowDropDownIcon />}
                  >
                    {t('export.export')}
                  </Button>
                  <Menu {...bindMenu(exportMenu)}>
                    <MenuItem
                      onClick={() => {
                        exportMenu.close();
                        exportToExcel(participants as IEnrichedParticipant[]);
                      }}
                    >
                      {t('export.all')}
                    </MenuItem>
                    <MenuItem
                      disabled={sortedParticipants.length === 0}
                      onClick={() => {
                        exportMenu.close();
                        exportToExcel(sortedParticipants as IEnrichedParticipant[]);
                      }}
                    >
                      {t('export.filtered')}
                    </MenuItem>
                  </Menu>
                </>
              }
          />
        }
      >
        {!isLoadingParticipants && !isLoadingCheckins && participants.length === 0 && (
          <Empty
            icon={<PeopleOutlinedIcon />}
            title={t('empty.participantsAdmin.title')}
            description={t('empty.participantsAdmin.description')}
            primaryAction={{
              label: t('empty.addNew'),
              onClick: () => handleOpenParticipantEditorModal(),
            }}
          />
        )}
        {!isLoadingParticipants &&
          !isLoadingCheckins &&
          participants.length > 0 &&
          sortedParticipants.length === 0 && <ListFilterEmptyState />}
        {!!sortedParticipants.length && (
          <Box sx={ticketCardVirtualizedContainerSx} data-tour="admin-participants-list">
            {view === EView.grid && (
              <VirtualizedParticipantGrid
                participants={sortedParticipants}
                header={
                  <SignupTimelineSection
                    points={signupTimelinePoints}
                    showFilters={false}
                    chartHeight={220}
                  />
                }
                checkin={handleOpenCheckinModal}
                register={effectiveEditionId ? handleOpenRegistrationModal : undefined}
                editRegistration={effectiveEditionId ? handleEditRegistrationWithGuard : undefined}
                deleteRegistration={effectiveEditionId ? handleDeleteRegistration : undefined}
                onDeleteCheckin={handleDeleteCheckin}
                onUpdatePrice={isAdmin ? handleUpdatePrice : undefined}
                deleteParticipant={handleDeleteParticipant}
                editParticipant={handleOpenParticipantEditorModal}
                transferParticipant={handleTransferParticipant}
                registrationIds={registrationIds}
                adminTourMenuAnchorParticipantId={adminTourAnchorParticipantId}
                adminTourRegisterAnchorParticipantId={adminTourRegisterAnchorId}
                adminTourCheckinAnchorParticipantId={adminTourCheckinAnchorId}
              />
            )}
            {view === EView.table && (
              <>
                <Box sx={{ mb: 2 }}>
                  <SignupTimelineSection
                    points={signupTimelinePoints}
                    showFilters={false}
                    chartHeight={220}
                  />
                </Box>
                <ParticipantsTable
                  layout="my"
                  participants={sortedParticipants}
                  checkin={handleOpenCheckinModal}
                  register={effectiveEditionId ? handleOpenRegistrationModal : undefined}
                  editRegistration={effectiveEditionId ? handleEditRegistrationWithGuard : undefined}
                  deleteRegistration={effectiveEditionId ? handleDeleteRegistration : undefined}
                  onDeleteCheckin={handleDeleteCheckin}
                  deleteParticipant={handleDeleteParticipant}
                  editParticipant={handleOpenParticipantEditorModal}
                  transferParticipant={handleTransferParticipant}
                  registrationIds={registrationIds}
                />
              </>
            )}
          </Box>
        )}
      </DashboardListPageLayout>

      <CheckinSerialModal
        open={!!newCheckin}
        handleClose={clearNewCheckin}
        serialNo={newCheckin?.ser}
      />

      {contestantEditorModalOpen && (
        <ParticipantEditorModal
          id="contestant-editor"
          open={contestantEditorModalOpen}
          handleClose={handleCloseParticipantEditorModal}
          handleSave={saveParticipant}
          selectedParticipant={selectedParticipant}
        />
      )}

      <CheckinModal
        id="checkin"
        isLoading={isLoadingCheckins}
        open={checkinModalOpen && !!selectedCheckinParticipant}
        handleClose={handleCloseCheckinModal}
        handleSave={saveCheckin}
        selectedApplicant={selectedCheckinParticipant}
      />

      {transferModalOpen && (
        <TransferModal
          open={transferModalOpen}
          handleClose={handleCloseTransferModal}
          handleSave={saveTransfer}
          selectedParticipant={transferParticipant}
        />
      )}

      {registrationModalOpen && !!registrationParticipant && effectiveEditionId && (
        <RegistrationModal
          id="registration"
          eventName={eventName}
          isLoading={isLoadingRegistrations}
          open={registrationModalOpen}
          handleClose={handleCloseRegistrationModal}
          handleSave={saveRegistration}
          selectedParticipant={registrationParticipant}
          existingRegistration={selectedRegistration}
          priceEditable={isAdmin}
          editionId={effectiveEditionId}
        />
      )}

      {isAdmin && (
        <QrCheckinScannerDialog
          open={qrScannerOpen}
          onClose={() => setQrScannerOpen(false)}
          onScan={handleQrCheckinScan}
        />
      )}

      {isAdmin && (
        <QrCheckinActionDialog
          open={!!qrScanResult}
          result={qrScanResult}
          onClose={() => setQrScanResult(null)}
          onCheckInSingle={handleQrCheckInSingle}
          onCheckInAllForUser={handleQrCheckInAllForUser}
        />
      )}

      {isAdmin && activeEditionId && (
        <UserCheckinWizardModal
          open={userCheckinWizardOpen}
          editionId={activeEditionId}
          initialUserId={userCheckinWizardInitialUserId}
          onClose={() => {
            setUserCheckinWizardOpen(false);
            setUserCheckinWizardInitialUserId(undefined);
          }}
          onComplete={() => {
            void Promise.all([
              doFetchParticipants(fetchParticipantsOptions),
              doFetchCheckins(),
            ]);
          }}
        />
      )}

      <AdminParticipantsTourOfferDialog
        open={tourOfferOpen}
        onStart={handleTourStart}
        onDecline={handleTourDecline}
      />
    </>
  );
};

export default Participants;

